import { lazy, Suspense } from "react";
import CheckRounded from "@mui/icons-material/CheckRounded";
import { grey } from "@mui/material/colors";

import LoadingFallback from "../../../components/LoadingFallback";
import { useLoanSummaryView } from "./useLoanSummaryView";
import { VisibilityOutlined } from "@mui/icons-material";

const DropdownButton = lazy(
  () => import("../../../components/ui/buttons/DropdownButton"),
);

/**
 * SummaryViewSelector — header dropdown (next to External Sync) that switches
 * the loan drilldown's summary "view" (Default / Closer / …). Only renders for
 * users who actually have more than the default view available (admins, or a
 * user whose role matches one of the gated views) — see useLoanSummaryView.
 */
const SummaryViewSelector = () => {
  const { view, setView, availableViews, canSwitchView } = useLoanSummaryView();

  if (!canSwitchView) return null;

  const current =
    availableViews.find((v) => v.key === view) || availableViews[0];

  const options = availableViews.map((v) => ({
    label: `${v.label} View`,
    onClick: () => setView(v.key),
    endIcon:
      v.key === view ? <CheckRounded sx={{ color: "primary.main" }} /> : null,
  }));

  // Compact single button: [eye] {view} [caret]. Tooltip surfaces the intent
  // since the "View" text label was dropped to save room in the title row.
  return (
    <Suspense fallback={<LoadingFallback />}>
      <DropdownButton
        size="small"
        variant="outlined"
        iconTooltip="View layout"
        startIcon={<VisibilityOutlined />}
        options={options}
        sx={{
          fontSize: "11px",
          fontWeight: 500,
          minWidth: "32px",
          padding: "5px 10px",
          lineHeight: 1,
          color: grey[800],
          borderRadius: "8px",
          borderColor: grey[300],
          whiteSpace: "nowrap",
          textTransform: "none",
          svg: { fontSize: "13px" },
        }}
      >
        {current?.label || "Default"}
      </DropdownButton>
    </Suspense>
  );
};

export default SummaryViewSelector;
